import type { CommittedSegment } from "./transcriptAccumulator";

/**
 * Shape and size policy for the persisted voice draft, shared by the web
 * (localStorage) and native (Capacitor Preferences) stores.
 *
 * Kept separate from the store implementations so the native one can live in
 * src/native/ — where everything that touches a Capacitor plugin lives —
 * without the two modules importing each other in a cycle.
 */

export interface VoiceDraftV1 {
  version: 1;
  draftId: string;
  createdAt: number;
  updatedAt: number;
  /** `?workspaceId=` scope the recording started under, if any. */
  workspaceScopeId?: string;
  committedSegments: CommittedSegment[];
  liveText: string;
  totalDurationSec: number;
  /** "recording" = the app died mid-recording; recovery still lands on review. */
  status: "recording" | "review";
  /** Edits made on the review screen, persisted on the same throttle. */
  reviewText?: string;
}

export interface DraftStorage {
  load(): Promise<VoiceDraftV1 | null>;
  save(draft: VoiceDraftV1): Promise<void>;
  clear(): Promise<void>;
}

export const VOICE_DRAFT_STORAGE_KEY = "cohesive.voiceDraft.v1";

/** UserDefaults is soft-capped around 4MB; keep the draft far under it. */
const MAX_PERSISTED_TEXT_CHARS = 512_000;

export function capDraft(draft: VoiceDraftV1): VoiceDraftV1 {
  const totalChars =
    draft.committedSegments.reduce((n, s) => n + s.text.length, 0) +
    draft.liveText.length +
    (draft.reviewText?.length ?? 0);
  if (totalChars <= MAX_PERSISTED_TEXT_CHARS) return draft;
  // A 3-hour dictation is ~200KB, so this is a pathological guard: drop the
  // oldest committed text first and mark the truncation.
  const segments = [...draft.committedSegments];
  let over = totalChars - MAX_PERSISTED_TEXT_CHARS;
  while (over > 0 && segments.length > 0) {
    over -= segments[0].text.length;
    segments.shift();
  }
  return {
    ...draft,
    committedSegments: [
      { text: "[earlier transcript truncated]", reason: "drop_rescue" },
      ...segments,
    ],
  };
}

/**
 * Drop-in store for surfaces that want the engine's resilience but no
 * persistence — the web composer, whose draft lifecycle the composer itself
 * owns, and which has no recovery UI to surface a stored draft through.
 */
export class EphemeralDraftStorage implements DraftStorage {
  async load(): Promise<VoiceDraftV1 | null> {
    return null;
  }
  async save(): Promise<void> {}
  async clear(): Promise<void> {}
}
